import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { useBusinessAuthContext } from "../commoncomponents/useBusinessAuthContext";
import API_URL from "../../api/API";
import WaiverPreview from "../commoncomponents/WaiverPreview";
import WaiverStepLayout from "./WaiverStepLayout";
import WAIVER_STEP_RULES from "./WaiverStepConfig";
import ConfirmModal from "../../components/ConfirmModal";

const BusinessSmartTemplateStep7 = () => {
  const { business } = useBusinessAuthContext();
  const navigate = useNavigate();
  const { waiverId } = useParams();

  const [waiverData, setWaiverData] = useState(null);  
  const [loading, setLoading] = useState(true);

  const [signerType, setSignerType] = useState("adult");
  const [waiverMode, setWaiverMode] = useState("full"); // "full" | "quick"
  const [device, setDevice] = useState("desktop");

  const [governingData, setGoverningData] = useState([]);
  
  const [isPublishing, setIsPublishing] = useState(false);

  const isPublished = waiverData?.waiver?.waiverStatus === "published"; //React re-renders when state updates (waiverData), so it will automatically recompute on every render. 
  
  const isArchived =
  waiverData?.waiver?.waiverStatus === "archived_by_admin" ||
  waiverData?.waiver?.waiverStatus === "archived_by_new_version" ||
  waiverData?.waiver?.waiverStatus === "archived_by_location_deletion" ;

  const showBackToBuilderLink = !isArchived;
  const showGoLiveLink = !isPublished && !isArchived;

  const [waiver, setWaiver] = useState(null); 
  const stepRules = WAIVER_STEP_RULES["step7"] || {};
  const statusRule = stepRules[waiver?.waiverStatus] || {};
  const isReadOnly = statusRule.readOnly ?? false;
  const readOnlyReason = statusRule.reason ?? "";

  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  const [otherMap, setOtherMap] = useState({}); 
  // { questionId: "text entered" }

  useEffect(() => {
    if (!business) {
      navigate("/business/login");
      return;
    }

    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL.BUSINESS_WAIVER_BY_ID}/${waiverId}?mode=preview`, {
          headers: {
            Authorization: `Bearer ${business.token}`,
            "x-session-id": business.sessionId,
            "x-frontend-url": window.location.pathname,
          },
        });

        const data = await res.json();

        if (data.success) {
          setWaiverData(data);
          setWaiver(data.waiver);
          
        }
      } catch (err) {
        console.error("Failed loading waiver", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [business, waiverId, navigate]);

  /* useEffect for Governing Law (same logic as Step-6)*/
  useEffect(() => {
    if (!waiverData?.waiver?.locations?.length) return;

    const loadGoverningLaws = async () => {
      try {
        // get all business locations
        const locRes = await fetch(API_URL.BUSINESS_LOCATIONS, {
          headers: {
            Authorization: `Bearer ${business.token}`,
          },
        });

        const locJson = await locRes.json();

        // only waiver locations
        /*const locations = locJson.locations.filter(loc =>
          waiverData.waiver.locations.includes(loc._id)
        );*/

        const waiverLocationIds = waiverData.waiver.locations.map(l =>
          String(typeof l === "string" ? l : l._id)
        );

        const locations = locJson.locations.filter(loc =>
          waiverLocationIds.includes(String(loc._id))
        );

        // unique states
        const states = [...new Set(locations.map(l => l.state))];

        // fetch waiver-specific clauses
        const clauseRes = await fetch(
          `${API_URL.GET_STATE_CLAUSES_FOR_WAIVER}/${waiverData.waiver._id}?states=${states.join(",")}`,
          {
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname,
            },
          }
        );

        const clauseJson = await clauseRes.json();

        // map per location
        const uiData = locations.map(loc => ({
          locationName: loc.name,
          state: loc.state,
          clause:
            clauseJson.data.find(
              c =>
                c.state.trim().toLowerCase() ===
                loc.state.trim().toLowerCase()
            )?.body ||
            "<p>No governing law found for this state.</p>",
        }));

        setGoverningData(uiData);
      } catch (err) {
        console.error("Failed loading governing laws (preview)", err);
      }
    };

    loadGoverningLaws();
  }, [waiverData, business.token]);

  /* Detect if any option text is exactly "other" (after waiverData loads).  Multiple questions may have other option. */
  useEffect(() => {
    if (!waiverData) return;

    const map = {};

    waiverData.waiver.questions?.forEach(q => {
      const hasOther = (q.options || []).some(
        opt => opt?.label?.trim().toLowerCase() === "other"
      );

      if (hasOther) {
        map[q._id] = ""; // initialize empty
      }
    });

    setOtherMap(map);
  }, [waiverData]);


  const handleSignerChange = (type) => {
    if (type === "minor" && !waiverData?.waiver?.minorWaiverAllowed) {
      alert("Minor waiver is not allowed for this template");
      return;
    }
    setSignerType(type);
  };

  const handleModeChange = (mode) => {
    if (
      mode === "quick" &&
      waiverData?.waiver?.returningSignerPolicy?.toLowerCase() !== "quick_resign"
    ) {
      alert("Quick resign is not enabled for this waiver");
      return;
    }

    setWaiverMode(mode);
  };

  const getPublishConfirmMessage = (status) => {
    if (status === "new_version") {
      return `Once published, this waiver will go live. If you make changes later, we’ll save them as a new version.`;
      }

      return `Once published, this waiver will go live. If you make changes later, we’ll save them as a new version.`;
  };

  const confirmPublish = async () => {

      setShowPublishConfirm(false);

      try {
        const res = await fetch(
          `${API_URL.BUSINESS_WAIVER_PUBLISH}/${waiverId}`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${business.token}`,
              "x-session-id": business.sessionId,
              "x-frontend-url": window.location.pathname,
            },
          }
        );

        const data = await res.json();
    
        if (data.success) {

          alert("Waiver published successfully");

          setWaiverData((prev) => ({
            ...prev,
            waiver: {
              ...prev.waiver,
              waiverStatus: "published",
            },
          }));

          navigate(`/business/waiverbuilder/smart/${waiverId}/step-8`);

        } else {
          setIsPublishing(false);
          alert(data.message || "Failed to publish waiver");
        }

        if (!res.ok) {
          setIsPublishing(false);
        }

      } catch (err) {
        setIsPublishing(false);
        console.error("Publish failed", err);
      }
    };

  const handlePublish = () => {

    if (isPublishing) return;

    const isInvalid = Object.values(otherMap).some(v => !v.trim());

    if (isInvalid) {
      alert('Please enter details for all "Other" fields before proceeding.');   
      return;
    }

    setIsPublishing(true);
    setShowPublishConfirm(true);
  };

  if (loading) return <div>Loading...</div>;

  const businessName = waiverData?.waiver?.businessId?.businessName || "";
  const businessEmail = waiverData?.waiver?.businessId?.email || "";
  const county =  waiverData?.waiver?.locations?.[0]?.county || "";

  const phValues = {
    BUSINESS_NAME: businessName,
    BUSINESS_EMAIL: businessEmail,
    COUNTY: county,
    MINOR_FULL_NAME: "the minor",
    MINOR_FIRST_NAME: "the minor",
    PREVIOUS_SIGNED_DATE: "THE PREVIOUS SIGNED DATE",
  };

  return (
    <>
    {/* 
    <div className="h-screen flex bg-background">
      <Sidebar />

      <div className="flex-1 overflow-auto py-10"> */ }
      <WaiverStepLayout  waiver={waiver} currentStep="step-7"  isReadOnly={isReadOnly} readOnlyReason={readOnlyReason} >
        
        <div className="max-w-6xl mx-auto px-6">

          <div className="flex items-center justify-between mb-6">
            {/* Back */}
            
            {!isPublished && (
              <button
                onClick={() =>
                  navigate(`/business/waiverbuilder/smart/${waiverId}/step-6`)
                }
                className="text-sm text-muted-foreground mb-2"
              >
                ← Previous Step
              </button>
              )}

            {/* Next (only when readOnly). But do not show live preview for non-published waivers, via Next Link */}
            {/* isReadOnly && !isPublished && (
              <button
                onClick={() =>
                  navigate(`/business/waiverbuilder/smart/${waiverId}/step-8`)
                }
                className="text-sm text-muted-foreground"
              >
                Next Step →
              </button>
            ) */}
          </div>

          {/* HEADER */}
          <div className="flex justify-between items-center mb-8">
            <div>            
              

              <h1 className="text-2xl font-semibold">Review & Publish</h1>
              <p className="text-sm text-muted-foreground">
                Review how signers will see your waiver
              </p>
            </div>

            {showGoLiveLink && !isReadOnly && (
              <button
                onClick={handlePublish}
                disabled={isPublishing}
                className={`px-6 py-3 rounded-full font-medium ${
                  isPublishing
                    ? "bg-gray-400 text-gray-700 cursor-not-allowed"
                    : "bg-yellow-500 hover:bg-yellow-600 text-black"
                }`}
              >
                {isPublishing ? "Publishing..." : "Go Live"}
              </button>
            )}
          </div>

          {/* CONTROLS */}
          <div className="flex justify-between items-center mb-10">
            <div className="flex gap-4">
              {/* SIGNER TYPE */}
              <div className="flex border rounded-full overflow-hidden">
                <button
                  onClick={() => handleSignerChange("adult")}
                  className={`px-4 py-2 text-sm ${
                    signerType === "adult" ? "bg-black text-white" : ""
                  }`}
                >
                  Adult
                </button>
                <button
                  onClick={() => handleSignerChange("minor")}
                  className={`px-4 py-2 text-sm ${
                    signerType === "minor" ? "bg-black text-white" : ""
                  } ${
                    !waiverData?.waiver?.minorWaiverAllowed
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  Minor
                </button>
              </div>

              {/* FULL / QUICK */}
              {waiverData?.waiver?.returningSignerPolicy?.toLowerCase() === "quick_resign" && (
              <div className="flex border rounded-full overflow-hidden">
                <button
                  onClick={() => handleModeChange("full")}
                  className={`px-4 py-2 text-sm ${
                    waiverMode === "full" ? "bg-black text-white" : ""
                  }`}
                >
                  Full
                </button>

                
                <button
                  onClick={() =>
                    /*waiverData?.waiver?.returningSignerPolicy?.toLowerCase() === "quick_resign" &&
                    */handleModeChange("quick")
                  }
                  className={`px-4 py-2 text-sm ${
                    waiverMode === "quick" ? "bg-black text-white" : ""
                  } ${   

                    waiverData?.waiver?.returningSignerPolicy?.toLowerCase() !== "quick_resign"
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  Quick
                </button>
                

              </div>
              )}

              {/* DEVICE */}
              <div className="flex border rounded-full overflow-hidden">
                <button
                  onClick={() => setDevice("mobile")}
                  className={`px-4 py-2 text-sm ${
                    device === "mobile" ? "bg-black text-white" : ""
                  }`}
                >
                  Mobile
                </button>
                <button
                  onClick={() => setDevice("desktop")}
                  className={`px-4 py-2 text-sm ${
                    device === "desktop" ? "bg-black text-white" : ""
                  }`}
                >
                  Desktop
                </button>
              </div>

            </div>

            <div className="text-sm text-gray-500">
              {isArchived
                ? "This waiver is archived and cannot be edited."
                : isPublished
                ? "This waiver is live and cannot be edited."
                : "You can update this waiver at any time."}
            </div>

          </div>

          {/* PREVIEW */}
          <div className="flex justify-center">
            
            <WaiverPreview
              key={`${signerType}_${waiverMode}`}  // ✅ FORCE REMOUNT if any of these 2 are changed
              waiverData={waiverData}
              signerType={signerType}
              device={device}
              waiverMode={waiverMode}
              governingData={governingData}
              phValues={phValues}
              otherMap={otherMap}
              setOtherMap={setOtherMap}
            />
              
          </div>
          

        </div>
      </WaiverStepLayout> 

      {showPublishConfirm && (
        <ConfirmModal
          open={showPublishConfirm}
          title="Confirm Publish"
          message={getPublishConfirmMessage(waiver?.waiverStatus)}
          confirmText="Publish"
          cancelText="Cancel"
          onConfirm={confirmPublish}
          onCancel={() => {
            setShowPublishConfirm(false);
            setIsPublishing(false);
          }}
        />
      )}

      {  /* </div>
    </div> */ }
    </>
  );
};

export default BusinessSmartTemplateStep7;